import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';
import type { IndicatorResponse } from '@/lib/schema/types';
import {
  type ItemStatus,
  type ObservationMetadata,
  type ObservationRecord,
} from './types';
import { getObservation, upsertObservation } from '@/lib/storage/db';
import { getItem } from '@/lib/schema/loader';
import { emptyRecord, migrateRecord } from './record';
import {
  ObservationContext,
  type ObservationContextValue,
} from './useObservation';

/**
 * Observation state machine + autosave.
 *
 * All mutations flow through `dispatch`. Every reducer result is persisted
 * to IndexedDB via a debounced effect so drafts survive refreshes.
 */

type State = ObservationRecord;

type Action =
  | { type: 'HYDRATE'; payload: ObservationRecord }
  | { type: 'SET_METADATA'; payload: Partial<ObservationMetadata> }
  | {
      type: 'SET_RESPONSE';
      itemId: string;
      indicatorId: string;
      response: IndicatorResponse | null;
    }
  | { type: 'SET_ITEM_NA'; itemId: string; value: boolean }
  | { type: 'SET_ITEM_NOTE'; itemId: string; note: string }
  | { type: 'COMPLETE_ITEM'; itemId: string }
  | { type: 'REOPEN_ITEM'; itemId: string }
  | { type: 'MARK_SUBMITTED'; submittedAt: number };

function bump(state: State): State {
  return { ...state, updatedAt: Date.now() };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'HYDRATE':
      return action.payload;
    case 'SET_METADATA':
      return bump({
        ...state,
        metadata: { ...state.metadata, ...action.payload },
      });
    case 'SET_RESPONSE': {
      const next = { ...state.responses };
      if (action.response == null) delete next[action.indicatorId];
      else next[action.indicatorId] = action.response;
      const nextStatus: Record<string, ItemStatus> = { ...state.itemStatus };
      // If clearing brought the item back to zero responses, revert to
      // 'untouched' so the sidebar dot disappears and the Landing "X/33
      // complete" count stays accurate.
      const item = getItem(action.itemId);
      const hasAnyResponse =
        item?.indicators.some((ind) => next[ind.id] !== undefined) ?? false;
      if (!hasAnyResponse && !(state.itemNa[action.itemId] ?? false)) {
        delete nextStatus[action.itemId];
      } else {
        nextStatus[action.itemId] = 'in_progress';
      }
      return bump({ ...state, responses: next, itemStatus: nextStatus });
    }
    case 'SET_ITEM_NA': {
      const nextItemNa = { ...state.itemNa, [action.itemId]: action.value };
      const nextStatus: Record<string, ItemStatus> = { ...state.itemStatus };
      if (action.value) {
        // Toggling NA on marks the item effectively handled — surface as
        // 'complete' so the sidebar/Landing counters reflect that.
        nextStatus[action.itemId] = 'complete';
      } else if (nextStatus[action.itemId] === 'complete') {
        // Toggling NA off while previously marked complete: drop back so
        // the observer can decide to either sweep the item or re-confirm.
        const item = getItem(action.itemId);
        const hasAnyResponse =
          item?.indicators.some(
            (ind) => state.responses[ind.id] !== undefined,
          ) ?? false;
        if (hasAnyResponse) nextStatus[action.itemId] = 'in_progress';
        else delete nextStatus[action.itemId];
      }
      return bump({
        ...state,
        itemNa: nextItemNa,
        itemStatus: nextStatus,
      });
    }
    case 'SET_ITEM_NOTE':
      return bump({
        ...state,
        itemNotes: { ...state.itemNotes, [action.itemId]: action.note },
      });
    case 'COMPLETE_ITEM':
      return bump({
        ...state,
        itemStatus: { ...state.itemStatus, [action.itemId]: 'complete' },
      });
    case 'REOPEN_ITEM':
      return bump({
        ...state,
        itemStatus: { ...state.itemStatus, [action.itemId]: 'in_progress' },
      });
    case 'MARK_SUBMITTED':
      return bump({
        ...state,
        status: 'submitted',
        submittedAt: action.submittedAt,
      });
  }
}

export function ObservationProvider({
  observationId,
  children,
}: {
  observationId?: string;
  children: ReactNode;
}) {
  const [state, dispatch] = useReducer(
    reducer,
    undefined,
    () => emptyRecord(observationId),
  );
  const hydratedRef = useRef(false);

  // Hydrate from IndexedDB on mount (if an id was passed and a record exists).
  useEffect(() => {
    if (!observationId) {
      hydratedRef.current = true;
      return;
    }
    let cancelled = false;
    getObservation(observationId).then((rec) => {
      if (cancelled) return;
      // Drafts outlive releases — normalise the shape before it reaches the
      // reducer so a record saved by an older build cannot crash the form.
      if (rec) dispatch({ type: 'HYDRATE', payload: migrateRecord(rec) });
      hydratedRef.current = true;
    });
    return () => {
      cancelled = true;
    };
  }, [observationId]);

  // Autosave (debounced) — only after hydration to avoid stomping drafts.
  useEffect(() => {
    if (!hydratedRef.current) return;
    const handle = setTimeout(() => {
      void upsertObservation(state);
    }, 300);
    return () => clearTimeout(handle);
  }, [state]);

  const setMetadata = useCallback(
    (patch: Partial<ObservationMetadata>) =>
      dispatch({ type: 'SET_METADATA', payload: patch }),
    [],
  );
  const setResponse = useCallback(
    (
      itemId: string,
      indicatorId: string,
      response: IndicatorResponse | null,
    ) => dispatch({ type: 'SET_RESPONSE', itemId, indicatorId, response }),
    [],
  );
  const setItemNa = useCallback(
    (itemId: string, value: boolean) =>
      dispatch({ type: 'SET_ITEM_NA', itemId, value }),
    [],
  );
  const setItemNote = useCallback(
    (itemId: string, note: string) =>
      dispatch({ type: 'SET_ITEM_NOTE', itemId, note }),
    [],
  );
  const completeItem = useCallback(
    (itemId: string) => dispatch({ type: 'COMPLETE_ITEM', itemId }),
    [],
  );
  const reopenItem = useCallback(
    (itemId: string) => dispatch({ type: 'REOPEN_ITEM', itemId }),
    [],
  );
  const markSubmitted = useCallback(
    () => dispatch({ type: 'MARK_SUBMITTED', submittedAt: Date.now() }),
    [],
  );

  const value = useMemo<ObservationContextValue>(
    () => ({
      state,
      setMetadata,
      setResponse,
      setItemNa,
      setItemNote,
      completeItem,
      reopenItem,
      markSubmitted,
    }),
    [
      state,
      setMetadata,
      setResponse,
      setItemNa,
      setItemNote,
      completeItem,
      reopenItem,
      markSubmitted,
    ],
  );

  return (
    <ObservationContext.Provider value={value}>
      {children}
    </ObservationContext.Provider>
  );
}
